/**
 * Delete Actions - Snippet deletion with local state update
 *
 * Calls the Block Service API to delete the snippet, then removes it from
 * the react-sweet-state store so the UI updates immediately, and from the
 * quick insert menu when editor API is provided in options. When options (flags, intl)
 * are provided, shows success/error flags like createActions and updateActions.
 */
import type { StoreActionApi } from 'react-sweet-state';
import type { IntlShape } from 'react-intl';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert/quick-insert-plugin-type';

import { isExperimentEnabled } from '@confluence/feature-experiments';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import * as BlockServiceClient from '../../services/blockServiceClient.ts';
import { messages } from '../../modals/DeleteSnippetModal.i18n.ts';
import { reportError } from '../../utils/reportError.ts';
import { removeSnippetQuickInsertItem } from '../../quick-insert/snippetQuickInsertItemApi.tsx';
import { getSnippetQuickInsertKey } from '../../utils/quickInsertUtils.ts';
import type { SnippetsState } from '../snippetsStore.types.ts';
import { showSnippetErrorFlag, showSnippetSuccessFlag } from '../../utils/snippetFlags.ts';

/**
 * Options for deleteSnippet that enable UI feedback (success/error flags)
 * and editor API for removing the snippet from the quick insert menu.
 */
export type DeleteSnippetOptions = {
	/** Flags API for showing success/error notifications */
	flags: WithFlagsProps['flags'];
	/** Intl for message formatting */
	intl: IntlShape;
	/** Editor API (e.g. from EditorContextPanelContext) to remove deleted snippet from quick insert; pass null when not available */
	editorAPI: PublicPluginAPI<[QuickInsertPlugin]> | null;
};

/**
 * Actions for deleting snippets.
 */
export const deleteActions = {
	/**
	 * Deletes a snippet by ID.
	 *
	 * - Calls the Block Service API with DELETE
	 * - On success: removes the snippet from local state and from quick insert menu; shows success flag if options provided
	 * - On error: reports to monitoring, shows error flag if options provided, then re-throws
	 *
	 * @param snippetId - The snippet ID (uuidv7) to delete
	 * @param options - Optional flags and intl for success/error UI feedback
	 */
	deleteSnippet:
		(snippetId: string, options?: DeleteSnippetOptions) =>
		async ({ getState, setState }: StoreActionApi<SnippetsState>): Promise<void> => {
			try {
				await BlockServiceClient.deleteSnippet(snippetId);

				const { snippets } = getState();
				const deletedSnippet = snippets.find((s) => s.snippetId === snippetId);

				setState({
					snippets: snippets.filter((s) => s.snippetId !== snippetId),
				});

				if (options && deletedSnippet) {
					showSnippetSuccessFlag(
						options.flags,
						options.intl.formatMessage(messages.deleteTemplateModalSuccessFlagTitle, {
							snippetName: deletedSnippet.name,
						}),
						options.intl.formatMessage(messages.deleteTemplateModalSuccessFlagDescription),
					);
				}

				const editorAPI = options?.editorAPI ?? null;
				if (isExperimentEnabled('platform_editor_slash_command')) {
					removeSnippetQuickInsertItem({ editorAPI, snippetId });
				} else {
					const removeQuickInsertItem =
						editorAPI?.quickInsert?.commands?.removeQuickInsertItem ?? null;
					if (removeQuickInsertItem) {
						editorAPI?.core?.actions.execute(
							removeQuickInsertItem(getSnippetQuickInsertKey(snippetId)),
						);
					}
				}
			} catch (thrownError) {
				const err = thrownError instanceof Error ? thrownError : new Error(String(thrownError));
				reportError(err);
				if (options) {
					showSnippetErrorFlag(
						options.flags,
						options.intl.formatMessage(messages.deleteTemplateModalErrorFlagTitle),
						options.intl.formatMessage(messages.deleteTemplateModalErrorFlagDescription),
					);
				}
				throw err;
			}
		},
};
